<template>
  <div class="account-code-selector">
    <div class="selector-wrapper" ref="selectorRef">
      <div
        class="selector-input"
        :class="{ 'open': isOpen, 'missing': !modelValue && props.required }"
        @mousedown.stop="toggleDropdown"
        :title="selectedCodeLabel"
      >
        <div class="selected-value">
          <span>{{ selectedCodeLabel || $t('accountCodes.selectPlaceholder') }}</span>
        </div>
        <LineIcon
          name="chevron-down"
          :size="11"
          :stroke-width="1.8"
          class="dropdown-icon"
          :class="{ 'rotate': isOpen }"
        />
      </div>
    </div>
    
    <!-- Teleport dropdown to body to escape z-index stacking context -->
    <Teleport to="body">
      <!-- Backdrop to catch clicks when dropdown is open -->
      <div v-if="isOpen" class="dropdown-backdrop" @mousedown.stop="closeDropdown"></div>
      
      <div v-if="isOpen" ref="dropdownRef" class="dropdown-menu" :style="dropdownStyle">
        <!-- Search Input -->
        <div class="search-wrapper">
          <LineIcon name="search" :size="15" :stroke-width="1.8" class="search-icon" />
          <input
            ref="searchInputRef"
            v-model="searchTerm"
            type="text"
            class="search-input"
            :placeholder="$t('accountCodes.selectorSearch')"
            @mousedown.stop
            @click.stop
          />
          <button
            v-if="searchTerm"
            @mousedown.stop="clearSearch"
            class="clear-search"
            type="button"
          >
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>
        
        <!-- Scrolling moves the rows out from under the card, which would leave it
             pointing at nothing. -->
        <div class="dropdown-list" @scroll="hideKeywords">
          <div
            v-if="!modelValue && !searchTerm"
            class="dropdown-item placeholder"
            @mousedown.stop="selectCode(null)"
          >
            <div class="code-info">{{ $t('accountCodes.selectPlaceholder') }}</div>
          </div>
          
          <div v-if="filteredAccountCodes.length === 0" class="dropdown-item no-results">
            <div class="code-info">No matching account codes found</div>
          </div>
          
          <div
            v-for="code in filteredAccountCodes"
            :key="code.id"
            class="dropdown-item"
            :class="{ 'selected': code.account_code === modelValue }"
            @mousedown.stop="selectCode(code.account_code)"
            @mouseenter="showKeywords(code, $event)"
            @mouseleave="hideKeywords"
          >
            <div class="code-info">
              <span class="code-number">{{ code.account_code }}</span>
              <span class="code-separator">-</span>
              <span class="code-name">{{ code.account_name }}</span>
              <!-- While searching these are the barcodes that matched, so it is
                   visible why the row is in the list; otherwise a short preview. -->
              <span v-if="code.hints.length" class="code-barcodes">
                <span class="code-separator">-</span>
                <span class="barcode-label">Barcode</span>
                <span v-for="hint in code.hints" :key="hint" class="mapping-chip">{{ hint }}</span>
                <span v-if="code.extraHints" class="mapping-more">+{{ code.extraHints }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Description keywords, revealed on hover so they never crowd the row.
           Tied to isOpen as well: clicking a row unmounts it before mouseleave
           can fire, which would otherwise strand the card on screen. -->
      <div
        v-if="isOpen && keywordPopover"
        class="keyword-popover"
        :style="keywordPopoverStyle"
      >
        <div class="keyword-popover-head">
          <LineIcon name="tag" :size="15" :stroke-width="1.8" />
          <span>Description mapping</span>
          <span class="keyword-popover-count">{{ keywordPopover.keywords.length }}</span>
        </div>
        <div class="keyword-popover-list">
          <span
            v-for="keyword in keywordPopover.keywords"
            :key="keyword"
            class="keyword-pill"
            :class="{ matched: isMatchedKeyword(keyword) }"
          >{{ keyword }}</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed, onUnmounted, watch, inject, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, null],
    default: ''
  },
  accountCodes: {
    type: Array,
    required: true
  },
  required: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'selected']);

const isOpen = ref(false);
const selectorRef = ref(null);
const dropdownRef = ref(null);
const searchInputRef = ref(null);
const searchTerm = ref('');
const dropdownId = ref(null);
const dropdownStyle = ref({});

// Inject active dropdown management
const activeDropdownId = inject('activeDropdownId', ref(null));
const setActiveDropdown = inject('setActiveDropdown', () => {});

// Generate unique ID for this dropdown
onMounted(() => {
  dropdownId.value = Math.random().toString(36).substring(7);
});

// Watch for other dropdowns opening
watch(activeDropdownId, (newActiveId) => {
  if (newActiveId !== dropdownId.value && isOpen.value) {
    isOpen.value = false;
    hideKeywords();
  }
});

const selectedCodeLabel = computed(() => {
  if (!props.modelValue) return '';
  const code = props.accountCodes.find(c => c.account_code === props.modelValue);
  return code ? `${code.account_code} - ${code.account_name}` : '';
});

// Mappings arrive as arrays of keywords/barcodes, but some callers hand them over
// as a comma-separated string or as raw JSON. Flatten whatever shape shows up into
// a plain list so one search covers them all.
const toList = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
  if (typeof value === 'object') return Object.values(value).map(v => String(v).trim()).filter(Boolean);
  return String(value).split(',').map(v => v.trim()).filter(Boolean);
};

const searchableText = (value) => toList(value).join(' ');

// How many mapping chips fit on a row before collapsing into "+N".
const MAX_HINTS = 3;

const filteredAccountCodes = computed(() => {
  const search = searchTerm.value.toLowerCase();

  const matches = search
    ? props.accountCodes.filter(code =>
        [
          code.account_code,
          code.account_name,
          code.internal_code,
          code.category,
          code.barcode_mapping,
          code.description_mapping,
        ].some(field => searchableText(field).toLowerCase().includes(search))
      )
    : props.accountCodes;

  // Puts whatever the search hit at the front, so the reason a row matched is the
  // part that survives truncation.
  const matchedFirst = (values) => {
    if (!search) return values;
    const hit = values.filter(v => v.toLowerCase().includes(search));
    return hit.length ? [...hit, ...values.filter(v => !hit.includes(v))] : values;
  };

  return matches.map((code) => {
    const barcodes = matchedFirst(toList(code.barcode_mapping));
    const keywords = matchedFirst(toList(code.description_mapping));

    return {
      ...code,
      hints: barcodes.slice(0, MAX_HINTS),
      extraHints: Math.max(0, barcodes.length - MAX_HINTS),
      // Not rendered in the row — shown in the hover card, so a code with twenty
      // keywords stays exactly as tall as one with none.
      keywords,
    };
  });
});

// Hover popover for the description keywords. Kept out of the row so a code with
// twenty keywords is no taller than one with none.
const keywordPopover = ref(null);
const keywordPopoverStyle = ref({});

const showKeywords = (code, event) => {
  if (!code.keywords.length) return;

  const rect = event.currentTarget.getBoundingClientRect();
  const WIDTH = 260;
  const GAP = 10;

  // Prefer the right of the row; flip to the left when it would run off-screen.
  const spaceRight = window.innerWidth - rect.right - GAP;
  const left = spaceRight >= WIDTH ? rect.right + GAP : Math.max(GAP, rect.left - WIDTH - GAP);

  keywordPopover.value = code;
  keywordPopoverStyle.value = {
    top: `${rect.top}px`,
    left: `${left}px`,
    width: `${WIDTH}px`,
    // Never taller than the space below it; the list scrolls instead.
    maxHeight: `${Math.max(120, window.innerHeight - rect.top - GAP)}px`,
  };
};

const hideKeywords = () => {
  keywordPopover.value = null;
};

const isMatchedKeyword = (keyword) =>
  !!searchTerm.value && keyword.toLowerCase().includes(searchTerm.value.toLowerCase());

const clearSearch = () => {
  searchTerm.value = '';
  searchInputRef.value?.focus();
};

const updateDropdownPosition = () => {
  if (!selectorRef.value) return;
  
  const rect = selectorRef.value.getBoundingClientRect();
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`
  };
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    setActiveDropdown(dropdownId.value);
    updateDropdownPosition();
    // Focus search input after dropdown opens
    setTimeout(() => {
      searchInputRef.value?.focus();
    }, 50);
  } else {
    searchTerm.value = '';
    hideKeywords();
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchTerm.value = '';
  hideKeywords();
  if (activeDropdownId.value === dropdownId.value) {
    setActiveDropdown(null);
  }
};

const selectCode = (accountCode) => {
  emit('update:modelValue', accountCode);
  emit('selected', accountCode);
  closeDropdown();
};

const handleClickOutside = (event) => {
  // Check if click is outside both the selector and the dropdown menu
  const isOutsideSelector = selectorRef.value && !selectorRef.value.contains(event.target);
  const isOutsideDropdown = dropdownRef.value && !dropdownRef.value.contains(event.target);
  
  if (isOutsideSelector && isOutsideDropdown) {
    closeDropdown();
  }
};

watch(isOpen, (newValue) => {
  if (newValue) {
    requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    });
  } else {
    document.removeEventListener('mousedown', handleClickOutside, true);
  }
});

// Watch for changes in accountCodes prop
watch(() => props.accountCodes, (newCodes) => {
  console.log('AccountCodeSelector - Account codes updated, count:', newCodes?.length);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside, true);
});
</script>

<style scoped>
.account-code-selector {
  width: 240px;
  min-width: 240px;
  max-width: 240px;
}

.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: transparent;
}

.selector-wrapper {
  position: relative;
  width: 100%;
  z-index: 10000;
}

.selector-input {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 36px 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 38px;
  max-height: 38px;
  width: 100%;
  z-index: 10001;
}

.selector-input:hover {
  border-color: #d1d5db;
}

.selector-input.missing {
  border-color: #fb923c;
  background-color: #fff7ed;
}

.selector-input.missing:hover {
  border-color: #f97316;
  background-color: #ffedd5;
}

.selector-input.open {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.selected-value {
  flex: 1;
  font-size: 13px;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.selected-value > span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.dropdown-icon {
  position: absolute;
  right: 12px;
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease;
  font-size: 11px;
  pointer-events: none;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.dropdown-menu {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 10002;
  max-height: 350px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.search-wrapper {
  position: relative;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
}

.search-input {
  width: 100%;
  padding: 8px 32px 8px 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  transition: all 0.2s ease;
  background: white;
}

.search-input:focus {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 12px;
  pointer-events: none;
}

.clear-search {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  border: none;
  border-radius: 50%;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 10px;
}

.clear-search:hover {
  background: #d1d5db;
  color: #1f2937;
}

.dropdown-list {
  max-height: 292px;
  overflow-y: auto;
  flex: 1;
}

.dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #f9fafb;
}

.dropdown-item.selected {
  background-color: #eff6ff;
  color: #1e40af;
}

.dropdown-item.placeholder {
  color: #9ca3af;
  font-style: italic;
}

.dropdown-item.no-results {
  color: #9ca3af;
  font-style: italic;
  text-align: center;
  padding: 16px 12px;
}

.code-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.code-number {
  font-weight: 600;
  color: #1f2937;
}

.code-separator {
  color: #9ca3af;
}

.code-name {
  color: #6b7280;
  /* Shrinks when the row is tight, but does not grow — otherwise it would push
     the barcode away from the name to the far edge of the row. */
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.code-barcodes {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.barcode-label {
  color: #6b7280;
}

/* Styled like the account code itself — both are identifiers, so they read the
   same weight and colour rather than one looking like a tag pinned on. */
.mapping-chip {
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
}

.mapping-more {
  color: #9ca3af;
}

/* Hover card for the description keywords — same language as the app's cards:
   white surface, soft border, rounded corners, blue accent. */
.keyword-popover {
  position: fixed;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 16px 40px -12px rgba(31, 41, 51, 0.28);
  padding: 12px 14px;
  pointer-events: none;
  overflow: hidden;
}

.keyword-popover-head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-bottom: 9px;
  margin-bottom: 9px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #2b82f0;
}

.keyword-popover-count {
  margin-left: auto;
  padding: 0 6px;
  border-radius: 4px;
  background: rgba(43, 130, 240, 0.09);
  font-size: 10.5px;
  line-height: 16px;
  letter-spacing: 0;
}

.keyword-popover-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  overflow-y: auto;
}

.keyword-pill {
  padding: 3px 9px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  line-height: 17px;
}

/* The keyword the search actually hit. */
.keyword-pill.matched {
  background: rgba(43, 130, 240, 0.1);
  color: #1d4ed8;
  font-weight: 600;
}

.dropdown-item.selected .code-number,
.dropdown-item.selected .code-name {
  color: #1e40af;
}

.internal-code-badge-small {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  white-space: nowrap;
  margin-left: auto;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(102, 126, 234, 0.3);
}

/* Scrollbar styling */
.dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* ── At the product's scale ────────────────────────────────────────────
 *
 * The field matched nothing else: 38px tall with its own greys, and when a
 * value was still missing it turned amber — a colour this product uses
 * nowhere. Same hairline field as everywhere, and "still to be filled" is
 * said by the edge, in the warning colour the rest of the app uses. */
.selector-input {
  min-height: 32px;
  padding: 6px 28px 6px 10px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  font-size: 13px;
}

.selector-input:hover {
  border-color: var(--primary, #0697e4);
}

.selector-input.missing {
  border-color: #f0b429;
  background-color: #fff;
}

.selector-input.missing:hover {
  border-color: #d99a0b;
  background-color: #fff;
}

.selector-input.open {
  border-color: var(--primary, #0697e4);
  box-shadow: none;
}

.selected-value {
  font-size: 13px;
}
</style>
